import {nanoid} from 'nanoid/non-secure';
import {logError} from 'src/utils/observability/analytics';

type MetricParams = Record<string, string | number | boolean>;
type GaugeParams = Record<string, number>;
interface IMetrics {
  increment: (metric: string, tags?: Record<string, unknown>) => void;
  gauge: (
    metric: string,
    measurements: GaugeParams,
    tags?: Record<string, unknown>,
  ) => void;
  set: (
    metric: string,
    values: MetricParams,
    tags?: Record<string, unknown>,
  ) => void;
}

let consecutiveFailures = 0;
const MAX_CONSECUTIVE_FAILURES = 5;

// we really just want to send a request to AEL https://go/ael#manual-requests
export const sendAELEvent = async (
  clientId: string,
  metric: string,
  data: Record<string, unknown>,
): Promise<void> => {
  if (consecutiveFailures >= MAX_CONSECUTIVE_FAILURES) {
    return;
  }

  const body = new URLSearchParams();
  body.append('client_id', clientId);
  body.append('event_name', metric);
  body.append('event_id', nanoid());
  body.append('created', Math.floor(Date.now() / 1000).toString());

  Object.entries(data).forEach(([key, value]) => {
    if (typeof value === 'string') {
      body.append(key, value);
    } else if (typeof value === 'number') {
      body.append(key, value.toString());
    } else if (typeof value === 'boolean') {
      body.append(key, value ? 'true' : 'false');
    }
  });

  try {
    await fetch('https://r.stripe.com/0', {
      headers: {
        'content-type': 'application/x-www-form-urlencoded;charset=UTF-8',
      },
      body,
      method: 'POST',
      credentials: 'include',
    });
    consecutiveFailures = 0;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (err) {
    consecutiveFailures++;
    // fail silently, don't leak unresolved runtime errors
  }
};

// copied relevant parts from @sail/observability
export interface ObservabilityConfig {
  service: string;
  project: string;
  analytics: {
    parametersAllowList: string[];
  };
}

export class ObservabilityMetrics implements IMetrics {
  constructor(private config: ObservabilityConfig) {
    if (!config) {
      logError(new Error('Observability config is required'));
    }
  }

  increment(metric: string, tags: Record<string, unknown> = {}): void {
    sendAELEvent(this.config.service, metric, tags);
  }

  gauge(
    metric: string,
    measurements: GaugeParams,
    tags: Record<string, unknown> = {},
  ): void {
    sendAELEvent(this.config.service, metric, {
      ...measurements,
      ...tags,
    });
  }

  set(
    metric: string,
    values: MetricParams,
    tags: Record<string, unknown> = {},
  ): void {
    sendAELEvent(this.config.service, metric, {
      ...values,
      ...tags,
    });
  }
}
